import { useSyncExternalStore } from 'react';

/**
 * A Patch in a small window of its own while the person is in another tab or app, like Google
 * Meet's: the film strip with Mute and Leave (components/PatchDock.tsx draws it there). It uses the
 * browser's Document Picture-in-Picture (Chrome and Edge 116 and later), opened with Pop out, or by
 * the browser itself when the tab is hidden during the Patch, where it offers that (the media
 * session's "enterpictureinpicture" action, for sites using the camera or microphone). It closes when
 * the Patch ends or the person comes back to the tab. Where the browser has none of this, Pop out
 * doesn't show and nothing else changes.
 *
 * The window is a document of this page's own, so it's never part of live help or a session
 * recording (and is marked private all the same).
 */

interface DocumentPip extends EventTarget {
  readonly window: Window | null;
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}

const api = (): DocumentPip | null =>
  typeof window === 'undefined' ? null : ((window as unknown as { documentPictureInPicture?: DocumentPip }).documentPictureInPicture ?? null);

/** This browser can put the Patch in a window of its own. */
export const pipSupported = (): boolean => Boolean(api());

let win: Window | null = null;
let opening = false;
/** The tab was hidden while the window was open: coming back closes it. */
let away = false;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of [...listeners]) l();
};

/** The Patch's own window, while it's open. */
export function usePipWindow(): Window | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => win,
    () => null
  );
}

/**
 * The app's look in the new window: its style sheets (from this site, as its security policy asks),
 * theme and language. Settles once the style sheets have loaded (at most 5 seconds, on a busy
 * computer), so the strip never shows unstyled; the window is dark meanwhile.
 */
function dress(doc: Document): Promise<void> {
  const loads: Promise<void>[] = [];
  for (const el of document.querySelectorAll<HTMLElement>('link[rel="stylesheet"], style')) {
    const copy = el.cloneNode(true) as HTMLElement;
    if (copy instanceof HTMLLinkElement && el instanceof HTMLLinkElement) {
      copy.href = el.href;
      loads.push(
        new Promise<void>((ok) => {
          copy.addEventListener('load', () => ok(), { once: true });
          copy.addEventListener('error', () => ok(), { once: true });
        })
      );
    }
    doc.head.appendChild(copy);
  }
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme) doc.documentElement.setAttribute('data-theme', theme);
  doc.documentElement.lang = document.documentElement.lang || 'en';
  doc.title = 'Patch';
  doc.body.className = 'patch-pip-body';
  doc.body.setAttribute('data-private', '');
  doc.documentElement.style.background = '#0f0d0c';
  return Promise.race([Promise.all(loads).then(() => undefined), new Promise<void>((ok) => setTimeout(ok, 5000))]);
}

/**
 * Opens the Patch's window (`height` to fit the tiles there are now; the person can resize it).
 * Must be called straight from a click, or from the browser's own picture-in-picture action.
 */
export function openPip(height = 420): void {
  const p = api();
  if (!p || win || opening) return;
  opening = true;
  let asked: Promise<Window>;
  try {
    asked = p.requestWindow({ width: 280, height: Math.round(Math.min(Math.max(height, 220), 720)) });
  } catch {
    opening = false;
    return;
  }
  asked.then(
    async (w) => {
      await dress(w.document);
      opening = false;
      // Closed again while its look was loading.
      if (w.closed) return;
      win = w;
      away = document.visibilityState === 'hidden';
      w.addEventListener('pagehide', () => {
        if (win !== w) return;
        win = null;
        emit();
      });
      emit();
    },
    () => {
      // Refused (no click to go with it, or the browser said no): the Patch stays in the tab.
      opening = false;
    }
  );
}

export function closePip(): void {
  const w = win;
  win = null;
  away = false;
  if (w) {
    try {
      w.close();
    } catch {
      // Already gone.
    }
    emit();
  }
}

/**
 * While in a Patch: lets the browser open the window itself when the person moves to another tab
 * (where it does that), and stops when the Patch ends.
 */
export function offerAutoPip(on: boolean): void {
  if (!api() || typeof navigator === 'undefined' || !navigator.mediaSession) return;
  try {
    navigator.mediaSession.setActionHandler('enterpictureinpicture' as MediaSessionAction, on ? () => openPip() : null);
  } catch {
    // This browser doesn't know that action.
  }
}

if (typeof document !== 'undefined') {
  // Back in the tab: the Patch is right here again, so its window closes.
  document.addEventListener('visibilitychange', () => {
    if (!win) return;
    if (document.visibilityState === 'hidden') away = true;
    else if (away) closePip();
  });
}
